import { useEffect, useState, useRef } from "react";
import { adminApi, formatApiError, ADMIN_API } from "@/admin/api";
import PageHeader, { Btn } from "@/admin/components/PageHeader";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple } from "@phosphor-icons/react";

export default function ContactsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [interested, setInterested] = useState("");
  const [skip, setSkip] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/contacts", { params: { q, interested, skip, limit: 50 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [skip, interested]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("cad4_admin_token");
      const res = await fetch(`${ADMIN_API}/contacts/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cad4surveys-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const importCsv = async (f) => {
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await adminApi.post("/contacts/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted}, skipped ${data.skipped}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-contacts-page">
      <PageHeader
        eyebrow="Inbox"
        title="Contact submissions"
        description="Inquiries from the public /contact form."
        actions={
          <>
            <Btn variant="ghost" onClick={exportCsv} data-testid="contacts-export">
              <DownloadSimple size={14} weight="bold" /> Export CSV
            </Btn>
            <Btn variant="ghost" onClick={() => fileRef.current?.click()} data-testid="contacts-import">
              <UploadSimple size={14} weight="bold" /> Import CSV
            </Btn>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => importCsv(e.target.files?.[0])}
              data-testid="contacts-import-input"
            />
          </>
        }
      />
      <div className="p-8 space-y-6">
        <form
          onSubmit={(e) => { e.preventDefault(); setSkip(0); load(); }}
          className="bg-white border border-zinc-200 p-4 flex gap-3 items-end flex-wrap"
        >
          <div className="flex-1 min-w-[240px]">
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-1">Search</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="name, email, company…"
              className="w-full border border-zinc-300 p-2 text-sm focus:outline-none focus:border-[#0044FF]"
              data-testid="contacts-q"
            />
          </div>
          <div className="min-w-[200px]">
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-1">Interested service</label>
            <input
              value={interested}
              onChange={(e) => setInterested(e.target.value)}
              placeholder="e.g. boundary-survey"
              className="w-full border border-zinc-300 p-2 text-sm"
            />
          </div>
          <Btn type="submit">Apply</Btn>
        </form>

        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                <th className="p-3">Received</th>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Company</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Services</th>
                <th className="p-3">Free trial?</th>
                <th className="p-3">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50 align-top">
                  <td className="p-3 text-[11px] font-mono text-zinc-500">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="p-3 font-medium text-zinc-900">{c.name}</td>
                  <td className="p-3 text-zinc-700">{c.email}</td>
                  <td className="p-3 text-zinc-700">{c.company || "—"}</td>
                  <td className="p-3 text-zinc-700 font-mono text-xs">{c.phone || "—"}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.interested_services || []).map((s) => (
                        <span key={s} className="px-2 py-0.5 text-[10px] bg-zinc-100 text-zinc-700 font-mono">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-[11px] font-mono">{c.is_free_trial ? "Yes" : "—"}</td>
                  <td className="p-3 text-xs text-zinc-600 max-w-sm truncate" title={c.message || ""}>{c.message || "—"}</td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={8} className="p-10 text-center text-zinc-500 text-sm">No submissions yet.</td></tr>
              )}
            </tbody>
          </table>
          <div className="p-3 border-t border-zinc-200 flex items-center justify-between text-xs font-mono text-zinc-600">
            <span>{total} total · showing {items.length}</span>
            <div className="flex gap-2">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 50))} className="px-3 py-1 border border-zinc-300 disabled:opacity-40">Prev</button>
              <button disabled={skip + items.length >= total} onClick={() => setSkip(skip + 50)} className="px-3 py-1 border border-zinc-300 disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
